import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import {
  MilesDamageWaiverTypeEnum,
  MilesPricingStrategyEnum,
  tireCountOptionsList,
} from '../../../resources/base/models';
import { DistributionModels } from '../../../resources/base/models/distributionModel';
import { CustomerType } from '../../../resources/base/models/enums';
import {
  isFinancialProductAllowingWarrantyExtension,
  isProductAllowingDamagesWaiver,
  isProductAllowingOverruledRV,
} from '../../../resources/base/models/financialProduct';
import { VehicleType } from '../../../resources/base/models/vehicleTypes';
import { calculateQuote } from '../../../services/api/miles/miles-services';
import { roundNumber } from '../../../services/common/formatNew';
import { assertDefined, getEnumValues, removeEmptyFromObject } from '../../../services/common/miscUtils';
import { DeepPartial, SetState } from '../../../services/common/miscUtils.types';
import { staticSelector } from '../../../services/redux';
import { customSelectors } from '../../../services/redux/features/custom.slice';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import {
  quoteSelectors,
  setProductTypeRestore,
  setPromotionsWarningMessages,
  setQuoteData,
} from '../../../services/redux/features/quote.slice';
import { QuotationFormData } from '../../../types';
import { additionalProductsTranslationsMap } from '../../utils';
import {
  calculateAnnualMileage,
  calculateDownPaymentAmountPercentage,
  calculateFirstInstallmentAmountPercentage,
} from './calculations';

export const numberOfInsurancePersonQualifierName = 'Number of Person';

export enum InsuranceServiceName {
  'PERSONAL_CREDIT_INSURANCE_PRIVATE' = 'Personal Credit insurance Private',
}

export enum InsuranceServiceProductId {
  'PERSONAL_CREDIT_INSURANCE_PRIVATE' = '400901',
}

/**
 * Enum allowing to recognize certain tire-related services within
 * additionalProducts list by their name.
 */
export enum TireServiceName {
  'SUMMER_TIRES' = 'Summer tires',
  'WINTER_TIRES' = 'Winter tires',
  'ALL_SEASON_TIRES' = 'All season tires',
}

/**
 * - Qualifiers names to use in case qualifiers object is not provided from Miles
 * and hast to be created manually.
 * - Names need to by in sync with BO.
 */
export enum TireServiceQualifierName {
  'SUMMERTIRES_QTY_SETTING' = 'SUMMERTIRES_QTY_SETTING',
  'WINTERTIRES_QTY_SETTING' = 'WINTERTIRES_QTY_SETTING',
  'ALLSEASONTIRES_QTY_SETTING' = 'ALLSEASONTIRES_QTY_SETTING',
}

export enum InsuranceCustomerQualifierId {
  'APPLICANT' = '400435',
  'CO_APPLICANT' = '400436',
}

/**
 * Combinations of tire services that customer can pick.
 */
export enum TireServicesSelectionType {
  'SUMMER_TIRES' = 'SUMMER_TIRES',
  'WINTER_TIRES' = 'WINTER_TIRES',
  'SUMMER_AND_WINTER_TIRES' = 'SUMMER_AND_WINTER_TIRES',
  'ALL_SEASON_TIRES' = 'ALL_SEASON_TIRES',
}

/**
 * - Check if choice can be selected to incidate exclusion of the service.
 */
export const isChoiceAnExclusionOfService = (choice: QuoteSchema.Schemas.ChoicesGetQuoteItem) => {
  return ['Not included', 'Non inclus'].includes(choice.name ?? '');
};

/**
 * - Check if selection of this choice means that the service in general is included.
 */
export const isChoiceAnInclusionOfService = (choice: QuoteSchema.Schemas.ChoicesGetQuoteItem) => {
  return !isChoiceAnExclusionOfService(choice);
};

/**
 * - Map indicating tire services choice IDs that correspond with inclusion
 * of given service.
 * - Each of tire services has 2 choice and without this map it's unknown which mean what.
 */
export const tireServiceInclusionMap = {
  [TireServiceName.SUMMER_TIRES]: '400897',
  [TireServiceName.WINTER_TIRES]: '100324',
  [TireServiceName.ALL_SEASON_TIRES]: '401434',
} as const;

export const isWarrantyExtensionAdditionalProduct = (productName: string) => {
  return productName === 'Warranty Extension';
};

export const maxDownPaymentAmountPercentage = 15;

export const isLounProductSelected = (financialProduct: string) => {
  return financialProduct?.includes('LOU');
};

const ELIGIBILITY_REDUCTION_STEP = 25;
const ELIGIBILITY_MIN_VALUE = 25;

export const generateEligibilityDropdownOptions = (serviceBonus: number): { value: string; label: string }[] => {
  if (!Number.isFinite(serviceBonus) || serviceBonus < ELIGIBILITY_MIN_VALUE) return [];
  const options: { value: string; label: string }[] = [];

  for (let current = serviceBonus; current >= ELIGIBILITY_MIN_VALUE; current -= ELIGIBILITY_REDUCTION_STEP) {
    options.push({ value: String(current), label: `${current}` });
  }
  return options;
};

export const isAudiPromoEligibilityEditable = (
  manufacturer: string | undefined,
  selectedProduct: string,
  customerType: CustomerType | undefined,
  milesPricingStrategy: QuoteSchema.Schemas.MilesPricingStrategyEnumType | undefined,
  serviceBonus: number
): boolean =>
  manufacturer === 'Audi' &&
  selectedProduct === 'LOUN_FL' &&
  customerType === CustomerType.LEGAL_ENTITY &&
  milesPricingStrategy === MilesPricingStrategyEnum.PROMOTION &&
  serviceBonus >= ELIGIBILITY_MIN_VALUE;

/**
 * Values in 'calculate' request payload that should be deemed as empty/invalid
 * and removed before payload submission.
 */
const calculateRequestEmptyValuesDef: DeepPartial<QuoteSchema.Schemas.CalculateSalesQuoteRequest> = {
  financialProduct: {
    offers: [
      {
        calculation: {
          duration: 0,
          contractMileage: 0,
        },
      },
    ],
  },
  vehicleData: {
    discounts: [
      {
        discountAmount: {
          grossAmount: 0,
        },
        discountPercentage: 0,
      },
    ],
    overruledRV: {
      amount: {
        grossAmount: 0,
      },
    },
  },
};

const parseAdditionalProductsForMilesApi = (additionalProducts?: QuoteSchema.Schemas.AdditionalProductsGetQuote) => {
  return additionalProducts?.map((additionalProduct) => {
    const productIsSelected = additionalProduct.choices?.some(
      (choice) => isChoiceAnInclusionOfService(choice) && choice.isSelected
    );

    const shouldDeleteQualifiers = isWarrantyExtensionAdditionalProduct(additionalProduct.name!) || !productIsSelected;

    if (shouldDeleteQualifiers) {
      const _additionalProduct = { ...additionalProduct };
      delete _additionalProduct.qualifiers;
      return _additionalProduct;
    }

    return additionalProduct;
  }) as QuoteSchema.Schemas.AdditionalCalculateSalesQuoteRequest | undefined;
};

export const stripPromotionsFromValues = (values: QuotationFormData) => {
  return {
    ...values,
    selectedPromotions: undefined,
    deselectedPromotions: undefined,
  };
};

export const getCalculateRequest = (
  values: QuotationFormData,
  selectedAdditionalProducts?: QuoteSchema.Schemas.AdditionalProductsGetQuote,
  resetRV?: boolean
): QuoteSchema.Schemas.CalculateSalesQuoteRequest => {
  const discountAmount = Math.max(0, values.totalDiscountAmount - values._minDealerDiscountAmount);
  const contractRVAmount = values.contractRVAmount ?? 0;
  const discountPercentage = Math.max(0, values.totalDiscountPercentage - values._minDealerDiscountPercentage) / 100;
  const contractRVPercentage = Math.max(0, values.contractRVPercentage) / 100;

  const vehicleType = staticSelector(customSelectors.selectVehicleType);

  const additionalProducts = parseAdditionalProductsForMilesApi(selectedAdditionalProducts);

  const shouldApplyPromotionPricing =
    additionalProducts?.some((product) =>
      product?.choices?.some((choice) => choice.name === 'Essentiel VP' && choice.isSelected)
    ) && vehicleType === VehicleType.USED;

  const customerType = staticSelector(quoteSelectors.selectCustomerTypeFromQuote);

  const shouldResetFirstInstallmentAmount = !values.firstInstallmentIsIncreased && values.firstInstallmentAmount > 0;
  const shouldResetDownPaymentAmount = !values.firstInstallmentIsIncreased && values.downPaymentAmount > 0;

  const withPrimeCee = Boolean(values.milesPrimeCee) && values.milesPrimeCee !== 0;

  const request: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {
    financialProduct: {
      offers: [
        {
          ...(isProductAllowingDamagesWaiver(values.financialProduct, customerType!) && {
            milesDamageWaiverType: values.damagesWaiver,
          }),
          milesAgencyModelType: values.distributionModel,
          ...(values.selectedPromotions && {
            selectedPromotions: values.selectedPromotions,
          }),
          ...(values.deselectedPromotions?.length && {
            deselectedPromotions: values.deselectedPromotions,
          }),
          calculation: {
            duration: Number(values.duration),
            contractMileage: values.contractMileage,
            excessMileagePriceAmount: {
              grossAmount: values.excessMileagePriceAmount,
            },
            ...(values.firstInstallmentIsIncreased && {
              firstInstallmentAmount: {
                grossAmount: values.firstInstallmentAmount || null,
              },
              downPaymentAmount: {
                netAmount: values.downPaymentAmount,
              },
            }),
            ...(shouldResetFirstInstallmentAmount && {
              firstInstallmentAmount: { grossAmount: null },
            }),
            ...(shouldResetDownPaymentAmount && {
              downPaymentAmount: { netAmount: 0 },
            }),
            milesPricingStrategy: shouldApplyPromotionPricing
              ? MilesPricingStrategyEnum.PROMOTION
              : values.milesPricingStrategy,
            milesPackage: values.milesPackage,
            fixedDueDay: Number(values.withdrawalDate),
            milesBonusEcologic: values.milesBonusEcologic,
            purchaseOption: {
              // Only one of grossAmount or percentage will be set, never both. Submitting both is invalid.
              ...(contractRVPercentage > 0 ? { percentage: contractRVPercentage } : { grossAmount: contractRVAmount }),
            },
            oneOffCommissionPercentage: values.dealerEffort / 100,
          },
          ...(withPrimeCee && { milesPrimeCee: { grossAmount: values.milesPrimeCee } }),
          ...(additionalProducts && {
            additionalProducts,
          }),
          ...(values.milesWarrantyTypeDuration && {
            milesWarrantyTypeDuration: values.milesWarrantyTypeDuration,
          }),
        },
      ],
    },
    vehicleData: {
      discounts: [
        {
          type: 'FREE_DEALER_DISCOUNT',
          discountAmount: {
            grossAmount: discountAmount,
          },
          // Do not specify discountPercentage if disountPrice is present. Submitting both is invalid.
          discountPercentage: discountAmount > 0 ? undefined : discountPercentage,
          isSelected: discountAmount > 0 || discountPercentage > 0,
        },
      ],
      ...(isProductAllowingOverruledRV(values.financialProduct) && {
        overruledRV: {
          amount: {
            grossAmount: resetRV ? null : values.overruledRV,
          },
        },
      }),
    },
  };

  return removeEmptyFromObject(request, calculateRequestEmptyValuesDef, true);
};

const getCalculateRequestForNewlySelectedTemplate = (
  values: QuotationFormData
): QuoteSchema.Schemas.CalculateSalesQuoteRequest => {
  const request: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {
    financialProduct: {
      offers: [
        {
          milesAgencyModelType: values.distributionModel,
          calculation: {
            duration: Number(values.duration),
            contractMileage: values.contractMileage,
          },
          milesTemplateId: values.milesTemplateId,
        },
      ],
    },
    vehicleData: {
      overruledRV: {
        amount: {
          grossAmount: null,
        },
      },
    },
  };

  return removeEmptyFromObject(request, calculateRequestEmptyValuesDef, true);
};

export const setInsuranceCustomerQualifierFromResponse = (
  additionalProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[],
  setSelectedInsuranceApplicantOption: SetState<InsuranceCustomerQualifierId>
) => {
  const insuranceService = additionalProducts.find((product) =>
    getEnumValues(InsuranceServiceName).includes(product.name as InsuranceServiceName)
  );

  if (!insuranceService) return;

  const selectedQualifier = insuranceService?.qualifiers?.find(
    (qualifier) => qualifier.name === numberOfInsurancePersonQualifierName
  );

  if (!selectedQualifier) {
    return;
  }

  const defaultQualifierValue = InsuranceCustomerQualifierId.APPLICANT;

  const qualifierValue = (selectedQualifier.stringValue ?? defaultQualifierValue) as InsuranceCustomerQualifierId;

  setSelectedInsuranceApplicantOption(qualifierValue);
};

export const setAdditionalProductsInsuranceCustomerQualifiers = (
  additionalProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[],
  selectedInsuranceApplicantOption: InsuranceCustomerQualifierId
) => {
  const additionalProductsCopy = structuredClone(additionalProducts);
  additionalProductsCopy.forEach((product) => {
    const isInsuranceService = getEnumValues(InsuranceServiceName).includes(product.name as InsuranceServiceName);
    if (!isInsuranceService) return;

    const selectedQualifier = product?.qualifiers?.find(
      (qualifier) => qualifier.name === numberOfInsurancePersonQualifierName
    );

    if (!selectedQualifier) {
      return;
    }

    selectedQualifier.stringValue = selectedInsuranceApplicantOption;
  });

  return additionalProductsCopy;
};

/**
 * - Determine if given choice of tire services additional product
 * indicates that given tires are to be included or not.
 */
export const isTireChoiceAnInclusion = (tireServiceName: TireServiceName, choiceId: string) => {
  return tireServiceInclusionMap[tireServiceName] === String(choiceId);
};

/**
 * - This function accounts for relation between certain tire-related
 * additional services and ensures that services selection is logical.
 * - It has to run when selectedAllAdditionalProducts changes.
 */
export const setAdditionalProductsTireServicesQualifiers = (
  additionalProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[],
  values: QuotationFormData,
  setFieldValue: (fieldName: string, value: any) => void
) => {
  const additionalProductsCopy = structuredClone(additionalProducts);
  const { tireCountSummer, tireCountWinter, tireCountAllSeasons } = values;

  additionalProductsCopy.forEach((value: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem) => {
    const isTireService = getEnumValues(TireServiceName).includes(value.name as TireServiceName);
    if (!isTireService) return;

    const tireServiceName = value.name as TireServiceName;

    let tireCount = '0';
    const isSelected = !!value?.choices?.some((choice) => isChoiceAnInclusionOfService(choice) && choice.isSelected);
    let fieldName = '';
    let qualifierName = '';
    if (tireServiceName === TireServiceName.SUMMER_TIRES) {
      tireCount = tireCountSummer;
      fieldName = 'tireCountSummer';
      qualifierName = TireServiceQualifierName.SUMMERTIRES_QTY_SETTING;
    } else if (tireServiceName === TireServiceName.WINTER_TIRES) {
      tireCount = tireCountWinter;
      fieldName = 'tireCountWinter';
      qualifierName = TireServiceQualifierName.WINTERTIRES_QTY_SETTING;
    } else {
      tireCount = tireCountAllSeasons;
      fieldName = 'tireCountAllSeasons';
      qualifierName = TireServiceQualifierName.ALLSEASONTIRES_QTY_SETTING;
    }

    if (isSelected) {
      // Ensure newly selected tire type starts with non-empty count
      if (!(Number(tireCount) > 0)) {
        tireCount = tireCountOptionsList[0].value;
        setFieldValue(fieldName, tireCount);
      }
    }

    // Toggle this tires selection in additional products
    value.choices?.forEach((choice) => {
      if (isTireChoiceAnInclusion(tireServiceName, choice.id!)) {
        choice.isSelected = isSelected;
      } /* Excluded */ else {
        choice.isSelected = !isSelected;
      }
    });

    if (!value.qualifiers) {
      value.qualifiers = [{ name: qualifierName }];
    }

    value.qualifiers[0].stringValue = tireCount;
  });

  return additionalProductsCopy;
};

const getTiresCountFromAdditionalProducts = (
  additionallProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[],
  tireServiceType: TireServiceName
) => {
  const searchedTireService = additionallProducts.find((product) => product.name === tireServiceType);
  return String(searchedTireService?.qualifiers?.[0].stringValue ?? '');
};

export const updateTireCountsFromAdditionalProducts = (
  additionalProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[],
  setFieldValue: (fieldName: string, value: any) => void
) => {
  setFieldValue(
    'tireCountSummer',
    getTiresCountFromAdditionalProducts(additionalProducts, TireServiceName.SUMMER_TIRES)
  );
  setFieldValue(
    'tireCountWinter',
    getTiresCountFromAdditionalProducts(additionalProducts, TireServiceName.WINTER_TIRES)
  );
  setFieldValue(
    'tireCountAllSeasons',
    getTiresCountFromAdditionalProducts(additionalProducts, TireServiceName.ALL_SEASON_TIRES)
  );
};

export const getDiscountAmountOfType = (
  quote: QuoteSchema.Schemas.QuoteResponse,
  discountType: QuoteSchema.Schemas.DiscountType
) => {
  return (
    quote?.vehicleData?.discounts?.find((discount) => discount.type === discountType)?.discountAmount?.grossAmount ?? 0
  );
};

export const getDiscountPercentageOfType = (
  quote: QuoteSchema.Schemas.QuoteResponse,
  discountType: QuoteSchema.Schemas.DiscountType
) => {
  return (
    (quote?.vehicleData?.discounts?.find((discount) => discount.type === discountType)?.discountPercentage ?? 0) * 100
  );
};

const getExcessMileagePriceAmount = (quote: QuoteSchema.Schemas.QuoteResponse): number => {
  const productName = quote?.financialProduct?.offers?.[0]?.productName ?? '';
  if (productName.includes('LVDL')) {
    return 0;
  }
  return quote?.financialProduct?.offers?.[0]?.calculation?.excessMileagePriceAmount?.grossAmount ?? 0;
};

export const filterEligiblePromotions = (promotions: QuoteSchema.Schemas.Promotion[]) => {
  return promotions.filter((promo) => {
    const hasCorrectSubvention = promo.subventions?.some((s) =>
      ['400002', 'subvention_type_service', 'subvention_type_target_lease_price'].includes(s.milesSubventionTypeId!)
    );
    const hasCorrectReference = promo.milesExternalReference?.startsWith('AS');
    const isSoftViolation = promo.milesViolationSeverity?.description === 'Soft Violation';
    return hasCorrectSubvention && hasCorrectReference && !isSoftViolation;
  });
};

const getFormValuesFromQuote = (quote: QuoteSchema.Schemas.QuoteResponse): Partial<QuotationFormData> => {
  const offer = quote.financialProduct?.offers?.[0];
  const calculation = offer?.calculation;

  const recommendedRV = quote.vehicleData?.recommendedRV?.amount?.grossAmount ?? 0;
  const recommendedRVNetAmount = quote.vehicleData?.recommendedRV?.amount?.netAmount ?? 0;
  const overruledRVGrossAmount = quote.vehicleData?.overruledRV?.amount?.grossAmount;
  const overruledRVNetAmount = quote.vehicleData?.overruledRV?.amount?.netAmount ?? 0;
  const overruledRVPercentage =
    (quote.vehicleData?.overruledRV?.percentage ?? quote.vehicleData?.recommendedRV?.percentage ?? 0) * 100;
  const minDealerDiscountAmount = getDiscountAmountOfType(quote, 'MIN_DEALER_DISCOUNT');
  const minDealerDiscountPercentage = getDiscountPercentageOfType(quote, 'MIN_DEALER_DISCOUNT');
  const freeDealerDiscountAmount = getDiscountAmountOfType(quote, 'FREE_DEALER_DISCOUNT');
  const freeDealerDiscountPercentage = getDiscountPercentageOfType(quote, 'FREE_DEALER_DISCOUNT');
  const totalDiscountAmount = minDealerDiscountAmount + freeDealerDiscountAmount;
  const firstInstallmentAmount = calculation?.firstInstallmentAmount?.grossAmount ?? 0;
  const financedAmount = calculation?.grossLoanAmount?.grossAmount ?? 0;
  const downPaymentAmount = calculation?.downPaymentAmount?.netAmount ?? 0;
  const vehicleTotalCost = financedAmount + totalDiscountAmount;

  const contractRVAmount = calculation?.purchaseOption?.grossAmount ?? 0;
  const contractRVPercentage = (calculation?.purchaseOption?.percentage ?? 0) * 100;

  const eligeblePromotions = filterEligiblePromotions(offer?.promotions || []);

  const getMilesPricingStarategy = () => {
    const strategy = calculation?.milesPricingStrategy;

    if (eligeblePromotions.some((p) => p.isApplied)) {
      return MilesPricingStrategyEnum.PROMOTION;
    }

    if (strategy === MilesPricingStrategyEnum.PROMOTION) {
      if (eligeblePromotions.length <= 0 || !eligeblePromotions.find((p) => p.isApplied)) {
        return MilesPricingStrategyEnum.STANDARD;
      }
    }
    return strategy;
  };

  const selectedPromotions = eligeblePromotions?.filter((p) => p.isApplied).map((p) => p.milesPromotionId!);
  const deselectedPromotions = [];

  const contractMileage = calculation?.contractMileage ?? 0;
  const duration = quote?.financialProduct?.offers?.[0].calculation?.duration;
  const damagesWaiver = offer?.milesDamageWaiverType ?? MilesDamageWaiverTypeEnum[0];

  const spreadLeasePrice = calculation?.spreadLeasePrice?.grossAmount ?? 0;
  const subsequentInstallment = calculation?.milesFinrentalSpreadLeasePrice?.grossAmount ?? 0;
  const totalServicesCost = spreadLeasePrice - subsequentInstallment;
  const milesPrimeCee = offer?.milesPrimeCee?.grossAmount;

  return {
    duration,
    contractMileage: calculation?.contractMileage,
    annualMileage: calculateAnnualMileage(contractMileage, duration),
    milesTemplateId: offer?.milesTemplateId,
    totalServicesCost,
    distributionModel: offer?.milesAgencyModelType as DistributionModels,
    vehicleTotalCost,
    recommendedRV,
    overruledRV: overruledRVGrossAmount ?? recommendedRV,
    overruledRVNetAmount: overruledRVNetAmount > 0 ? overruledRVNetAmount : recommendedRVNetAmount,
    overruledRVPercentage,
    eligibility: roundNumber((calculation?.eligibility ?? 0) * 100),
    serviceBonus: roundNumber((calculation?.serviceBonus ?? 0) * 100),
    dealerEffort: roundNumber((calculation?.oneOffCommissionPercentage ?? 0) * 100),
    dealerEffortMax: roundNumber((calculation?.maxDealerLimit ?? 0) * 100),
    _minDealerDiscountAmount: minDealerDiscountAmount,
    _minDealerDiscountPercentage: minDealerDiscountPercentage,
    totalDiscountAmount,
    totalDiscountPercentage: minDealerDiscountPercentage + freeDealerDiscountPercentage,
    financedAmount,
    firstInstallmentAmount,
    firstInstallmentAmountPercentage: calculateFirstInstallmentAmountPercentage(firstInstallmentAmount, financedAmount),
    downPaymentAmount,
    downPaymentAmountPercentage: calculateDownPaymentAmountPercentage(downPaymentAmount, financedAmount),
    firstInstallmentIsIncreased: firstInstallmentAmount > 0 || downPaymentAmount > 0,
    milesPricingStrategy: getMilesPricingStarategy(),
    selectedPromotions,
    deselectedPromotions,
    milesPackage: calculation?.milesPackage,
    pricingId: calculation?.id,
    subsequentInstallment,
    spreadLeasePrice,
    milesBonusEcologic: calculation?.milesBonusEcologic,
    excessMileagePriceAmount: getExcessMileagePriceAmount(quote),
    damagesWaiver,
    contractRVAmount,
    contractRVPercentage,
    milesWarrantyTypeDuration: offer?.milesWarrantyTypeDuration,
    _milesWarrantyType: offer?.milesWarrantyType,
    milesPrimeCee,
  };
};

const setFieldsValues = <T extends Record<string, any>>(
  setFieldValue: (fieldName: string, value: any) => void,
  values: T
) => {
  Object.entries(values).forEach(([fieldName, newValue]) => {
    setFieldValue(fieldName, newValue);
  });
};

export const prefillQuotationForm = (
  quote: QuoteSchema.Schemas.QuoteResponse,
  setFieldValue: (fieldName: string, value: any) => void
) => {
  const additionalProducts = quote.financialProduct?.offers?.[0].additionalProducts ?? [];
  updateTireCountsFromAdditionalProducts(additionalProducts, setFieldValue);

  const newValues = getFormValuesFromQuote(quote);
  setFieldsValues(setFieldValue, newValues);

  return newValues;
};

const getLastCalculatedValuesString = (
  values: QuotationFormData,
  selectedAllAdditionalProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[]
) => {
  return `${JSON.stringify(values)}_{${JSON.stringify(selectedAllAdditionalProducts)}}`;
};

export const useQuoteCalculation = (
  isInitialPrefilDone: boolean,
  selectedAllAdditionalProducts: QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[]
) => {
  const quote = useSelector(quoteSelectors.selectQuote);
  const quoteId = assertDefined(useSelector(quoteSelectors.selectQuoteId));
  const { values, setFieldValue } = useFormikContext<QuotationFormData>();
  const dispatch = useDispatch();
  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);
  const [lastValidQuote, setLastValidQuote] = useState<QuoteSchema.Schemas.QuoteResponse>(quote);
  const [shouldUpdateLastCalculatedValuesString, setShouldUpdateLastCalculatedValuesString] = useState(false);
  const [lastCalculatedValuesString, setLastCalculatedValuesString] = useState('');
  const [isFormSyncedWithApi, setIsFormSyncedWithApi] = useState(false);
  const [milesWarnings, setMilesWarnings] = useState<QuoteSchema.Schemas.MilesWarning[]>();
  // User-started calculation is to be required in some cases from design-standpoint despite quote being synced with API.
  const [isCalculationArbitrarilyRequired, setIsCalculationArbitrarilyRequired] = useState(false);

  const [calculationPending, setCalculationPending] = useState(false);

  useEffect(() => {
    if (!shouldUpdateLastCalculatedValuesString) return;
    setLastCalculatedValuesString(getLastCalculatedValuesString(values, selectedAllAdditionalProducts));
    setShouldUpdateLastCalculatedValuesString(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, selectedAllAdditionalProducts, shouldUpdateLastCalculatedValuesString]);

  useEffect(() => {
    if (!lastCalculatedValuesString) return;
    setIsFormSyncedWithApi(
      lastCalculatedValuesString === getLastCalculatedValuesString(values, selectedAllAdditionalProducts)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, selectedAllAdditionalProducts, lastCalculatedValuesString]);

  useEffect(() => {
    // Ensure need to recalculate when locked quote became editable again.
    setIsFormSyncedWithApi(!isQuoteEditable);
  }, [isQuoteEditable]);

  const _calculateQuote = useCallback(
    async (
      _quoteId: string,
      request: QuoteSchema.Schemas.CalculateSalesQuoteRequest,
      resetPromotionWarning = true,
      force = false
    ) => {
      if (calculationPending && !force) return quote;
      if (!isQuoteEditable) return quote;
      dispatch(loadingActions.setLoading(true));

      setCalculationPending(true);

      return calculateQuote(_quoteId, request)
        .then((_quote) => {
          dispatch(setQuoteData(_quote));
          prefillQuotationForm(_quote, setFieldValue);
          if (resetPromotionWarning) {
            dispatch(setPromotionsWarningMessages([]));
          }

          if (_quote?.financialProduct?.offers?.[0]?.promotionsWarningMessages?.length && resetPromotionWarning) {
            dispatch(setPromotionsWarningMessages(_quote.financialProduct.offers[0].promotionsWarningMessages || []));

            if (_quote?.financialProduct?.offers?.[0]?.calculation?.milesPricingStrategy === 'PROMOTION') {
              request.financialProduct!.offers![0]!.calculation!.milesPricingStrategy = 'STANDARD';
              return _calculateQuote(_quoteId, request, false, true);
            }
          }

          const milesWarnings = _quote?.financialProduct?.offers?.[0]?.calculation?.milesWarnings;
          setMilesWarnings(milesWarnings);

          setLastValidQuote(structuredClone(_quote));

          const loadingId = dispatch(loadingActions.startContextualLoading()).payload.id;

          // Delay till next macrotask when additional products are fully updated
          setTimeout(() => {
            setShouldUpdateLastCalculatedValuesString(true);
            dispatch(loadingActions.endContextualLoading(loadingId));
          }, 1000);

          return _quote;
        })
        .catch(() => {
          const lastValidQuoteClone = structuredClone(lastValidQuote);

          dispatch(setQuoteData(lastValidQuoteClone));
          prefillQuotationForm(lastValidQuoteClone, setFieldValue);

          const productName = lastValidQuoteClone.financialProduct?.offers?.[0].productName;
          if (productName) {
            dispatch(setProductTypeRestore(productName));
          }

          return lastValidQuoteClone;
        })
        .finally(() => {
          dispatch(loadingActions.setLoading(false));
          setCalculationPending(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isQuoteEditable, calculationPending, lastValidQuote]
  );

  useEffect(() => {
    if (!isInitialPrefilDone) return;
    if (!values.milesTemplateId) return;
    if (!values.duration) return;
    if (!values.contractMileage) return;

    const milesTemplateIdChanged = values.milesTemplateId !== quote.financialProduct?.offers?.[0].milesTemplateId;

    setIsCalculationArbitrarilyRequired(true);

    if (milesTemplateIdChanged) {
      _calculateQuote(quoteId, getCalculateRequestForNewlySelectedTemplate(values));
      return;
    }

    const durationChanged = values.duration !== quote.financialProduct?.offers?.[0].calculation?.duration;
    const contractMileageChanged =
      values.contractMileage !== quote.financialProduct?.offers?.[0].calculation?.contractMileage;

    if (durationChanged || contractMileageChanged) {
      const resetRV = true;
      _calculateQuote(quoteId, getCalculateRequest(values, undefined, resetRV));
      return;
    }

    const milesAgencyModelTypeChanged =
      values.distributionModel !== quote.financialProduct?.offers?.[0].milesAgencyModelType;

    if (milesAgencyModelTypeChanged) {
      _calculateQuote(quoteId, getCalculateRequest(values));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.milesTemplateId, values.duration, values.contractMileage, values.distributionModel, isInitialPrefilDone]);

  useEffect(() => {
    // Quote was just locked or unlocked and lastValidQuote has to be updated.
    setLastValidQuote(quote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuoteEditable]);

  return {
    /**
     * - Calculation automatically triggered in response to changes to the form.
     */
    calculateQuoteAuto: ((...args: Parameters<typeof _calculateQuote>) => {
      return _calculateQuote(...args).then((result) => {
        setIsCalculationArbitrarilyRequired(true);
        return result;
      });
    }) as typeof _calculateQuote,
    /**
     * - Final calculation manually started by the user prior to quote validation.
     */
    calculateQuoteManualFinal: ((...args: Parameters<typeof _calculateQuote>) => {
      return _calculateQuote(...args).then((result) => {
        setMilesWarnings(undefined);
        setIsCalculationArbitrarilyRequired(false);
        return result;
      });
    }) as typeof _calculateQuote,
    isFormSyncedWithApi,
    isCalculationArbitrarilyRequired,
    milesWarnings,
    resetMilesWarnings: () => setMilesWarnings(undefined),
  };
};

export type CalculateQuote = ReturnType<typeof useQuoteCalculation>;

export const getAllowedValuesByRestriction = (
  restriction?: Partial<QuoteSchema.Schemas.OfferRestriction>
): number[] => {
  const min: number = parseFloat(restriction?.minimum || '0');
  const max: number = parseFloat(restriction?.maximum || '0');
  const step: number = parseFloat(restriction?.step || '1'); // Aplication hangs when restrictions object is not provided, it is a fallback to not lead to inifinite loop
  const additionalValues: number[] = restriction?.additionalValues ? restriction.additionalValues.map(Number) : [];

  const rangeValues: number[] = [];
  for (let value = min; value <= max; value += step) {
    rangeValues.push(value);
  }

  const allValues: number[] = Array.from(new Set([...rangeValues, ...additionalValues]));
  return allValues.sort((a, b) => a - b);
};

export const getClosestValue = (value: number, allowedValues: number[]): number => {
  let closestValue: number = allowedValues[0];
  let smallestDifference: number = Math.abs(value - allowedValues[0]);

  // eslint-disable-next-line no-restricted-syntax
  for (const allowedValue of allowedValues) {
    const difference: number = Math.abs(value - allowedValue);
    if (difference < smallestDifference) {
      closestValue = allowedValue;
      smallestDifference = difference;
    }
  }

  return closestValue;
};

export const adjustMileageToAllowedRange = (mileageValue: number, mileageAllowedValues?: number[]): number => {
  if (!mileageAllowedValues) {
    return 0;
  }
  const min: number = mileageAllowedValues[0];
  const max: number = mileageAllowedValues[mileageAllowedValues.length - 1];

  if (mileageValue < min) {
    return min;
  }

  if (mileageValue > max) {
    return max;
  }

  return getClosestValue(mileageValue, mileageAllowedValues);
};

/**
 * - List of additional products market requested as hidden
 * - Values in the list match prodcuts names
 */
export const additionalProductsToExclude = ['Dealer Tire storage', 'Tire storage'] as const;

export const getAdditionalProductLabel = (name: string = '') => {
  return additionalProductsTranslationsMap[name] || name;
};

export const getPermittedAdditionalProductsFromQuote = (quote: QuoteSchema.Schemas.QuoteResponse) => {
  const productName = quote.financialProduct?.offers?.[0].productName;

  // Quote wintout assigned financial product cannot have additional products
  if (!productName) return [];

  const filteredServicesWithChoices =
    quote?.financialProduct?.offers?.[0]?.additionalProducts?.filter((service) => {
      return !!service?.choices?.length;
    }) ?? [];

  const filteredPermittedServices =
    filteredServicesWithChoices.filter((service) => {
      const productToBeExcluded = additionalProductsToExclude.includes(service.name as any);

      if (productToBeExcluded) return false;

      if (isWarrantyExtensionAdditionalProduct(service.name!)) {
        return isFinancialProductAllowingWarrantyExtension(productName);
      }

      return true;
    }) ?? [];

  return filteredPermittedServices;
};
